import {
  REWARD_TYPES,
  REWARD_CARD_TYPES,
  PRODUCT_BASED_REWARD_TYPES,
  REWARD_STATUS
} from '@/utils/constants/rewardsConstants';
import {getFormattedDate, getDateFromISOString} from '@/utils/commons';
import {
  REWARD_FIELD_MAPPINGS,
  transformToUiValue
} from '@/utils/constants/fieldMappings';
import {Constants} from './constants/commonConstants';

// Re-export for consumers that import from this file
export {PRODUCT_BASED_REWARD_TYPES};

export const QUOTA_DECREASE_ERROR =
  'Quota cannot be decreased for active rewards.';

export const getQuotaDecreaseError = (value, originalQuota, isActive) => {
  if (!isActive) {
    return null;
  }

  const numValue = Number(value);
  const numOriginalQuota = Number(originalQuota);

  if (
    Number.isFinite(numValue) &&
    Number.isFinite(numOriginalQuota) &&
    numValue < numOriginalQuota
  ) {
    return QUOTA_DECREASE_ERROR;
  }

  return null;
};

/**
 * Computes leftover inventory when quota changes.
 * Draft/Published: sync leftover to quota.
 * Active: only add positive delta to original available; invalid edits keep original leftover.
 */
export const computeLeftoverForQuota = ({
  value,
  isInProgress,
  originalQuota,
  originalLeftOver
}) => {
  if (!isInProgress) {
    return value;
  }

  const numValue = Number(value);
  const numOriginalQuota = Number(originalQuota);
  const numOriginalLeftOver = Number(originalLeftOver);

  if (
    !Number.isFinite(numValue) ||
    !Number.isFinite(numOriginalQuota) ||
    !Number.isFinite(numOriginalLeftOver)
  ) {
    return originalLeftOver ?? '';
  }

  const delta = numValue - numOriginalQuota;
  if (delta > 0) {
    return String(numOriginalLeftOver + delta);
  }

  return originalLeftOver ?? '';
};

/**
 * Get the 'value' field from API data based on reward type
 * CASH uses rewardCashAmount, DISCOUNT/PEP_VOUCHER_DISCOUNT use discountAmount
 */
export const getValueFieldFromApiData = (rewardType, data) => {
  if (rewardType === REWARD_TYPES.CASH) {
    const cashAmount = data?.rewardCashAmount;
    if (cashAmount === 0 || cashAmount === null || cashAmount === undefined) {
      return '';
    }
    return String(cashAmount);
  }
  if (
    rewardType === REWARD_TYPES.DISCOUNT ||
    rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT
  ) {
    const discountAmount = data?.discountAmount;
    if (
      discountAmount === 0 ||
      discountAmount === null ||
      discountAmount === undefined
    ) {
      return '';
    }
    return String(discountAmount);
  }
  return '';
};

/**
 * Get quota value for payload based on reward type and card type
 * - Physical-only: quota goes to physicalQuota, so return empty for quota
 * - Both: digital uses quotaDigitalCard
 * - Digital-only and others: use quota directly
 */
export const getQuotaForPayload = (
  rewardType,
  cardType,
  quotaDigitalCard,
  quota
) => {
  if (rewardType === REWARD_TYPES.VISA_CARD) {
    if (cardType === REWARD_CARD_TYPES.Both) return quotaDigitalCard;
    if (cardType === REWARD_CARD_TYPES.Physical) return ''; // Physical uses physicalQuota
  }
  return quota;
};

/**
 * Get leftOverInventory value for payload based on reward type and card type
 * - Physical-only: leftOverInventory goes to physicalLeftOverInventory, so return empty
 * - Both: digital uses leftOverInventoryDigitalCard
 * - Digital-only and others: use leftOverInventory directly
 */
export const getLeftOverInventoryForPayload = (
  rewardType,
  cardType,
  leftOverInventoryDigitalCard,
  leftOverInventory,
  unlimitedInventory
) => {
  if (unlimitedInventory) return '';
  if (rewardType === REWARD_TYPES.VISA_CARD) {
    if (cardType === REWARD_CARD_TYPES.Both)
      return leftOverInventoryDigitalCard;
    if (cardType === REWARD_CARD_TYPES.Physical) return ''; // Physical uses physicalLeftOverInventory
  }
  return leftOverInventory;
};

/**
 * Get limitPerStore value for payload based on reward type and card type
 * - Physical-only: limitPerStore goes to physicalLimitPerStore, so return empty
 * - Both: digital uses limitPerStoreDigitalCard
 * - Digital-only and others: use limitPerStore directly
 */
export const getLimitPerStoreForPayload = (
  rewardType,
  cardType,
  limitPerStoreDigitalCard,
  limitPerStore
) => {
  if (rewardType === REWARD_TYPES.VISA_CARD) {
    if (cardType === REWARD_CARD_TYPES.Both) return limitPerStoreDigitalCard;
    if (cardType === REWARD_CARD_TYPES.Physical) return ''; // Physical uses physicalLimitPerStore
  }
  return limitPerStore;
};

/**
 * Get physical quota for payload based on card type
 * - Physical-only: UI quota → API physicalQuota
 * - Both: UI quotaPhysicalCard → API physicalQuota
 */
export const getPhysicalQuotaForPayload = (
  rewardType,
  cardType,
  quotaPhysicalCard,
  quota
) => {
  if (rewardType !== REWARD_TYPES.VISA_CARD) return '';
  if (cardType === REWARD_CARD_TYPES.Physical) return quota;
  if (cardType === REWARD_CARD_TYPES.Both) return quotaPhysicalCard;
  return '';
};

/**
 * Get physical leftOverInventory for payload based on card type
 * - Physical-only: UI leftOverInventory → API physicalLeftOverInventory
 * - Both: UI leftOverInventoryPhysicalCard → API physicalLeftOverInventory
 */
export const getPhysicalLeftOverInventoryForPayload = (
  rewardType,
  cardType,
  leftOverInventoryPhysicalCard,
  leftOverInventory,
  unlimitedInventory
) => {
  if (rewardType !== REWARD_TYPES.VISA_CARD) return '';
  if (unlimitedInventory) return '';
  if (cardType === REWARD_CARD_TYPES.Physical) return leftOverInventory;
  if (cardType === REWARD_CARD_TYPES.Both) return leftOverInventoryPhysicalCard;
  return '';
};

/**
 * Get physical limitPerStore for payload based on card type
 * - Physical-only: UI limitPerStore → API physicalLimitPerStore
 * - Both: UI limitPerStorePhysicalCard → API physicalLimitPerStore
 */
export const getPhysicalLimitPerStoreForPayload = (
  rewardType,
  cardType,
  limitPerStorePhysicalCard,
  limitPerStore
) => {
  if (rewardType !== REWARD_TYPES.VISA_CARD) return '';
  if (cardType === REWARD_CARD_TYPES.Physical) return limitPerStore;
  if (cardType === REWARD_CARD_TYPES.Both) return limitPerStorePhysicalCard;
  return '';
};

/**
 * Builds all inventory-related fields for the API payload in one place.
 * Consolidates the logic from 6 separate functions.
 *
 * @param {Object} formData - The form data object
 * @returns {Object} All inventory fields for the API payload
 */
export const buildInventoryPayload = (formData = {}) => {
  const {rewardType, cardType, unlimitedInventory} = formData;
  const isVisaCard = rewardType === REWARD_TYPES.VISA_CARD;
  const isPhysicalOnly = isVisaCard && cardType === REWARD_CARD_TYPES.Physical;
  const isBothTypes = isVisaCard && cardType === REWARD_CARD_TYPES.Both;

  // Helper to clear leftOverInventory when unlimited
  const getLeftOver = (value) => (unlimitedInventory ? '' : (value ?? ''));

  // Default empty result
  const emptyFields = {quota: '', leftOverInventory: '', limitPerStore: ''};
  const emptyPhysical = {
    physicalQuota: '',
    physicalLeftOverInventory: '',
    physicalLimitPerStore: ''
  };

  // Standard fields (quota, leftOverInventory, limitPerStore)
  const standardFields = isPhysicalOnly
    ? emptyFields
    : isBothTypes
      ? {
          quota: formData?.quotaDigitalCard ?? '',
          leftOverInventory: getLeftOver(
            formData?.leftOverInventoryDigitalCard
          ),
          limitPerStore: formData?.limitPerStoreDigitalCard ?? ''
        }
      : {
          quota: formData?.quota ?? '',
          leftOverInventory: getLeftOver(formData?.leftOverInventory),
          limitPerStore: formData?.limitPerStore ?? ''
        };

  // Physical fields (only for VISA_CARD)
  const physicalFields = !isVisaCard
    ? emptyPhysical
    : isPhysicalOnly
      ? {
          physicalQuota: formData?.quota ?? '',
          physicalLeftOverInventory: getLeftOver(formData?.leftOverInventory),
          physicalLimitPerStore: formData?.limitPerStore ?? ''
        }
      : isBothTypes
        ? {
            physicalQuota: formData?.quotaPhysicalCard ?? '',
            physicalLeftOverInventory: getLeftOver(
              formData?.leftOverInventoryPhysicalCard
            ),
            physicalLimitPerStore: formData?.limitPerStorePhysicalCard ?? ''
          }
        : emptyPhysical;

  return {...standardFields, ...physicalFields};
};

/**
 * Get discount amount for payload based on reward type
 */
export const getDiscountAmount = (rewardType, value) => {
  if (
    rewardType === REWARD_TYPES.DISCOUNT ||
    rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT
  ) {
    if (value === '' || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

/**
 * Get products to map based on reward type
 */
export const getProductsToMap = (
  rewardType,
  selectedProducts,
  selectedPepVouchersProducts
) => {
  const isBonusProducts = rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const isProductType = rewardType === REWARD_TYPES.PRODUCT;
  if (isProductType || isBonusProducts) {
    return selectedProducts;
  }
  return selectedPepVouchersProducts || selectedProducts;
};

/**
 * Get reward cash amount based on reward type
 * VISA_CARD uses rewardCashAmount directly, CASH uses value
 */
export const getRewardCashAmount = (rewardType, rewardCashAmount, value) => {
  if (rewardType === REWARD_TYPES.VISA_CARD) {
    if (
      rewardCashAmount === '' ||
      rewardCashAmount === null ||
      rewardCashAmount === undefined
    ) {
      return null;
    }
    const num = Number(rewardCashAmount);
    return Number.isFinite(num) ? num : null;
  }
  if (rewardType === REWARD_TYPES.CASH) {
    if (value === '' || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

// ============================================
// Load Draft Utilities
// ============================================

/**
 * Maps reward products from API to selectedProducts format
 */
export const mapRewardProductsToSelected = (rewardProducts) => {
  if (!Array.isArray(rewardProducts)) return [];
  return rewardProducts.map((p) => ({
    value: p.productId,
    label: p.productName || p.productId
  }));
};

/**
 * Maps reward products to fetchedProducts with details structure
 */
export const mapRewardProductsToFetched = (rewardProducts) => {
  if (!Array.isArray(rewardProducts)) return [];
  return rewardProducts.map((p) => {
    const isUnlimitedQuota =
      p.unlimitedQuota === true ||
      (p.unlimitedQuota == null && p.productQuota == null);

    let inventory = '';
    if (isUnlimitedQuota) {
      inventory = Constants.Unlimited;
    } else if (p.productInventory != null) {
      inventory = String(p.productInventory);
    }

    const quotaValue =
      p.productQuota != null && p.productQuota !== 0
        ? String(p.productQuota)
        : '';

    return {
      productId: p.productId,
      productName: p.productName || p.productId,
      priority: p.priority || 0,
      details: {
        points: p.productPoints === 0 ? '' : String(p.productPoints ?? ''),
        quota: {
          unlimited: isUnlimitedQuota,
          value: quotaValue
        },
        inventory: inventory,
        // ACTIVE baselines for Setup modal leftover/inventory parity with Attributes
        originalQuota: isUnlimitedQuota ? '' : quotaValue,
        originalInventory: inventory
      }
    };
  });
};

/**
 * Creates image array from URLs
 */
const createImageArray = (imageUrls = []) => [
  {position: 1, url: imageUrls[0] || '', file: null},
  {position: 2, url: imageUrls[1] || '', file: null},
  {position: 3, url: imageUrls[2] || '', file: null}
];

/**
 * Extracts image URLs from product display data
 * @param {Object} productDisplay - The product display object containing images per product
 * @param {string} productId - The product ID to extract images for
 * @returns {string[]|null} Array of image URLs or null if none found
 */
export const getProductImageUrls = (productDisplay, productId) => {
  const images = productDisplay?.[productId]?.images || [];
  const urls = images
    .filter((img) => img?.url && !String(img.url).startsWith('blob:'))
    .map((img) => img.url);
  return urls.length > 0 ? urls : null;
};

/**
 * Unified function to map product display for all product-based reward types
 * - BONUS_PRODUCTS: includes per-product rewardText
 * Priority: per-product imageUrls from rewardProducts > root-level imageUrls from data
 */
export const mapProductDisplay = (rewardProducts, data, rewardType) => {
  if (!Array.isArray(rewardProducts) || rewardProducts.length === 0) return {};

  const isBonusProducts = rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const productDisplay = {};

  rewardProducts.forEach((p) => {
    // Use per-product imageUrls if available, fallback to root-level imageUrls for backward compatibility
    const imageUrls = p.imageUrls || data?.imageUrls || [];
    productDisplay[p.productId] = {
      useDefaultImage: Boolean(p?.defaultImage),
      images: createImageArray(imageUrls),
      defaultImages: [],
      // Only BONUS_PRODUCTS has per-product rewardText
      ...(isBonusProducts && {rewardText: p.rewardText || {}})
    };
  });
  return productDisplay;
};

/**
 * Maps included segment groups from API to selectedSegments format
 */
export const mapIncludedSegmentGroups = (includedSegmentGroups) => {
  if (!Array.isArray(includedSegmentGroups)) return [];
  return includedSegmentGroups.map((group) =>
    (group.segments || []).map((s) => s.segmentName)
  );
};

/**
 * Maps excluded segments from API format
 */
export const mapExcludedSegments = (excludedSegments) => {
  if (!Array.isArray(excludedSegments)) return [];
  return excludedSegments.map((s) => s.segmentName);
};

/**
 * Converts ISO date to display format (MM/DD/YYYY)
 * Uses the same utility as sales targets for consistency
 */
const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  return getDateFromISOString(isoDate) || '';
};

/**
 * Gets inventory fields based on card type for VISA_CARD
 * - Digital-only: API uses quota/leftOverInventory/limitPerStore
 * - Physical-only: API uses physicalQuota/physicalLeftOverInventory/physicalLimitPerStore
 * - Both types use InventorySection which reads quota/leftOverInventory/limitPerStore
 */
const getInventoryFieldsForCardType = (apiRewardType, data) => {
  const isPhysicalOnly =
    apiRewardType === REWARD_TYPES.VISA_CARD && data?.cardType === 'Physical';

  // Physical-only: map physical* API fields to regular UI fields
  if (isPhysicalOnly) {
    const leftOverInventory = data?.unlimitedInventory
      ? 'Unlimited'
      : String(data?.physicalLeftOverInventory ?? '');
    return {
      quota: String(data?.physicalQuota ?? ''),
      leftOverInventory,
      limitPerStore: String(data?.physicalLimitPerStore ?? ''),
      originalQuota: String(data?.physicalQuota ?? ''),
      originalLeftOverInventory: leftOverInventory
    };
  }

  // Digital-only and all other types: use regular API fields
  const leftOverInventory = data?.unlimitedInventory
    ? 'Unlimited'
    : String(data?.leftOverInventory ?? '');
  return {
    quota: String(data?.quota ?? ''),
    leftOverInventory,
    limitPerStore: String(data?.limitPerStore ?? ''),
    originalQuota: String(data?.quota ?? ''),
    originalLeftOverInventory: leftOverInventory
  };
};

/**
 * Gets VISA_CARD "Both" type specific fields
 * Only returns fields when reward type is VISA_CARD with cardType Both
 */
const getVisaCardBothFields = (apiRewardType, data) => {
  const isVisaCardBoth =
    apiRewardType === REWARD_TYPES.VISA_CARD && data?.cardType === 'Both';

  if (!isVisaCardBoth) {
    return {};
  }

  const leftOverInventoryDigitalCard = data?.unlimitedInventory
    ? 'Unlimited'
    : String(data?.leftOverInventory ?? '');
  const leftOverInventoryPhysicalCard = data?.unlimitedInventory
    ? 'Unlimited'
    : String(data?.physicalLeftOverInventory ?? '');

  return {
    quotaDigitalCard: String(data?.quota ?? ''),
    leftOverInventoryDigitalCard,
    limitPerStoreDigitalCard: String(data?.limitPerStore ?? ''),
    quotaPhysicalCard: String(data?.physicalQuota ?? ''),
    leftOverInventoryPhysicalCard,
    limitPerStorePhysicalCard: String(data?.physicalLimitPerStore ?? ''),
    originalQuotaDigitalCard: String(data?.quota ?? ''),
    originalLeftOverInventoryDigitalCard: leftOverInventoryDigitalCard,
    originalQuotaPhysicalCard: String(data?.physicalQuota ?? ''),
    originalLeftOverInventoryPhysicalCard: leftOverInventoryPhysicalCard
  };
};

/**
 * Builds the complete form data object for loading a draft
 */
export const buildLoadDraftFormData = ({
  data,
  apiRewardType,
  rewardIdToLoad,
  mappedProducts,
  mappedFetchedProducts,
  mappedProductDisplay,
  mappedSegments,
  mappedExcludedSegments
}) => {
  const isPepVoucherProduct =
    apiRewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  const isBonusProducts = apiRewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const isProduct = apiRewardType === REWARD_TYPES.PRODUCT;

  let rewardCashAmount = '';
  if (apiRewardType === REWARD_TYPES.VISA_CARD) {
    if (data?.rewardCashAmount !== 0) {
      rewardCashAmount = String(data?.rewardCashAmount ?? '');
    }
  }

  return {
    // Basic info (name is handled by REWARD_FIELD_MAPPINGS)
    rewardId: String(data?.id || data?.rewardId || rewardIdToLoad),
    rewardType: apiRewardType,
    status: data?.status || '',

    // Auto-map fields using centralized configuration
    ...Object.entries(REWARD_FIELD_MAPPINGS).reduce(
      (acc, [uiField, config]) => {
        const apiValue = data?.[config.apiField];

        acc[uiField] = transformToUiValue(apiValue, config);
        return acc;
      },
      {}
    ),

    // Segments
    selectedSegments: mappedSegments,
    excludedSegments: mappedExcludedSegments,

    // Products
    selectedProducts: isBonusProducts || isProduct ? mappedProducts : [],
    selectedPepVouchersProducts: isPepVoucherProduct ? mappedProducts : [],
    fetchedProducts: isBonusProducts ? mappedFetchedProducts : [],

    // Visa card specific (cardType is handled by REWARD_FIELD_MAPPINGS)
    rewardCashAmount,

    // Cash/Discount/PepVoucher Discount specific
    value: getValueFieldFromApiData(apiRewardType, data),

    // Display step fields
    imageUrls: data?.imageUrls || [],
    rewardText: data?.rewardText || null,
    display:
      isBonusProducts || isProduct || isPepVoucherProduct
        ? {productDisplay: mappedProductDisplay}
        : {productDisplay: {}},

    // Attributes step fields
    // Convert dates from API (UTC) to local timezone format (MM/DD/YYYY) - same as sales targets
    // This ensures dates are displayed correctly in the user's local timezone
    startDate: getDateFromISOString(data?.startDate) || null,
    endDate: getDateFromISOString(data?.endDate) || null,
    startDateDisplay: formatDateForDisplay(data?.startDate),
    endDateDisplay: formatDateForDisplay(data?.endDate),

    // Inventory fields - UI field mapping based on card type:
    // Digital-only: API quota → UI quota (InventorySection)
    // Physical-only: API physicalQuota → UI quota (InventorySection)
    // Both: API quota → quotaDigitalCard, API physicalQuota → quotaPhysicalCard (InventorySectionForCardTypeBoth)
    ...getInventoryFieldsForCardType(apiRewardType, data),

    // Visa card "Both" type fields (only for VISA_CARD with cardType Both)
    ...getVisaCardBothFields(apiRewardType, data),
    updatedAt: getDateFromISOString(data?.updatedAt) || null,
    // Convert UTC to local timezone first (like SalesTarget), then format as yyyy-mm-dd
    updatedAtDisplay: getFormattedDate(
      getDateFromISOString(data?.updatedAt),
      false,
      '-',
      'yyyy-mm-dd'
    ),
    isPublished: data?.status === REWARD_STATUS.PUBLISHED,
    alertDisplayCount:
      data?.alertDisplayCount === null || data?.alertDisplayCount === undefined
        ? ''
        : data?.alertDisplayCount
  };
};
